import { ProTable } from "@ant-design/pro-components";
import { useRef, useState, useMemo } from "react";
import { Radio, Input, Space, Typography, Image, Modal, Button, Divider } from "antd";
import PageContainer from "@/pages/components/PageContainer";
import { getContentList } from "@/services/content";
const { Text } = Typography;

// URL验证函数
const isValidUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

// 增强的图片渲染组件，包含错误处理
const EnhancedImage = ({ src, alt, style, maxRetries = 2, ...props }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  // 如果URL无效，直接显示错误状态
  if (!isValidUrl(src)) {
    return (
      <div style={{
        ...style,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        border: '1px dashed #d9d9d9',
        borderRadius: 8,
        color: '#999',
        fontSize: 12,
        minHeight: 60,
        minWidth: 60,
        padding: 8
      }}>
        <div>无效链接</div>
      </div>
    );
  }

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
    setRetryCount(0);
  };

  const handleImageError = () => {
    setImageLoading(false);
    
    if (retryCount < maxRetries) {
      // 延迟重试
      setTimeout(() => {
        setRetryCount(prev => prev + 1);
        setImageLoading(true);
        setImageError(false);
      }, 1000 * (retryCount + 1)); // 递增延迟
    } else {
      setImageError(true);
    }
  };

  const handleRetry = () => {
    setRetryCount(0);
    setImageLoading(true);
    setImageError(false);
  };

  if (imageError) {
    return (
      <div style={{
        ...style,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        border: '1px dashed #d9d9d9',
        borderRadius: 8,
        color: '#999',
        fontSize: 12,
        minHeight: 60,
        minWidth: 60,
        padding: 8,
        cursor: 'pointer'
      }}
      onClick={handleRetry}
      title="点击重试"
      >
        <div>加载失败</div>
        <div style={{ fontSize: 10, marginTop: 4 }}>点击重试</div>
      </div>
    );
  }

  const finalSrc = `${src}${retryCount > 0 ? `?retry=${retryCount}&t=${Date.now()}` : ''}`;

  return (
    <div style={{ position: 'relative', ...style }}>
      {imageLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: 8,
          fontSize: 12,
          color: '#999'
        }}>
          <div>{retryCount > 0 ? `重试中(${retryCount}/${maxRetries})...` : '加载中...'}</div>
        </div>
      )}
      <Image
        src={finalSrc}
        alt={alt}
        onLoad={handleImageLoad}
        onError={handleImageError}
        style={{
          ...style,
          opacity: imageLoading ? 0 : 1,
          transition: 'opacity 0.3s'
        }}
        {...props}
      />
    </div>
  );
};

// 增强的视频组件，包含错误处理
const EnhancedVideo = ({ src, style, controls = true, preload = "metadata", ...props }) => {
  const [videoError, setVideoError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);

  // 如果URL无效，直接显示错误状态
  if (!isValidUrl(src)) {
    return (
      <div style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        border: '1px dashed #d9d9d9',
        borderRadius: 8,
        color: '#999',
        fontSize: 12,
        minHeight: 60,
        minWidth: 100
      }}>
        无效的视频链接
      </div>
    );
  }

  const handleVideoLoad = () => {
    setVideoLoading(false);
    setVideoError(false);
  };

  const handleVideoError = () => {
    setVideoLoading(false);
    setVideoError(true);
  };

  if (videoError) {
    return (
      <div style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        border: '1px dashed #d9d9d9',
        borderRadius: 8,
        color: '#999',
        fontSize: 12,
        minHeight: 60,
        minWidth: 100
      }}>
        视频加载失败
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', ...style }}>
      {videoLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: 8,
          fontSize: 12,
          color: '#999',
          zIndex: 1
        }}>
          视频加载中...
        </div>
      )}
      <video
        controls={controls}
        preload={preload}
        onLoadedData={handleVideoLoad}
        onError={handleVideoError}
        style={{
          ...style,
          opacity: videoLoading ? 0 : 1,
          transition: 'opacity 0.3s'
        }}
        {...props}
      >
        <source src={src} type="video/mp4" />
        <source src={src} type="video/webm" />
        <source src={src} type="video/quicktime" />
        <source src={src} type="video/x-msvideo" />
        您的浏览器不支持视频播放
      </video>
    </div>
  );
};

// 增强的音频组件，包含错误处理
const EnhancedAudio = ({ src, style, controls = true, preload = "metadata", ...props }) => {
  const [audioError, setAudioError] = useState(false);
  const [audioLoading, setAudioLoading] = useState(true);

  // 如果URL无效，直接显示错误状态
  if (!isValidUrl(src)) {
    return (
      <div style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        border: '1px dashed #d9d9d9',
        borderRadius: 8,
        color: '#999',
        fontSize: 12,
        minHeight: 40,
        minWidth: 200,
        padding: 8
      }}>
        无效的音频链接
      </div>
    );
  }

  const handleAudioLoad = () => {
    setAudioLoading(false);
    setAudioError(false);
  };

  const handleAudioError = () => {
    setAudioLoading(false);
    setAudioError(true);
  };

  if (audioError) {
    return (
      <div style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        border: '1px dashed #d9d9d9',
        borderRadius: 8,
        color: '#999',
        fontSize: 12,
        minHeight: 40,
        minWidth: 200,
        padding: 8
      }}>
        音频加载失败
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', ...style }}>
      {audioLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: 8,
          fontSize: 12,
          color: '#999',
          zIndex: 1
        }}>
          音频加载中...
        </div>
      )}
      <audio
        controls={controls}
        preload={preload}
        onLoadedData={handleAudioLoad}
        onError={handleAudioError}
        style={{
          width: '100%',
          minWidth: 200,
          height: 40,
          opacity: audioLoading ? 0 : 1,
          transition: 'opacity 0.3s',
          borderRadius: 4,
          ...style
        }}
        {...props}
      >
        <source src={src} type="audio/mpeg" />
        <source src={src} type="audio/wav" />
        <source src={src} type="audio/ogg" />
        <source src={src} type="audio/mp4" />
        <source src={src} type="audio/flac" />
        <source src={src} type="audio/aac" />
        您的浏览器不支持音频播放
      </audio>
    </div>
  );
};

export default function ContentList() {
  const actionRef = useRef();
  const [type, setType] = useState("APP");
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [isTraceModalOpen, setIsTraceModalOpen] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [showRawData, setShowRawData] = useState(false);

  // Define different column sets for each type
  const appColumns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 200, 
    },
    {
      title: "时间",
      dataIndex: "createTime",
      width: 200,
    },
    {
      title: "审核状态",
      dataIndex: "status",
      valueEnum: {
        "PASSED": {
          text: "通过",
          status: "Success",
        },
        "REJECTED": {
          text: "未通过",
          status: "Error",
        },
        "PENDING": {
          text: "待审核",
          status: "Warning",
        },
      },
      width: 100,
    },
    {
      title: "IP",
      dataIndex: "clientIp",
      width: 100,
    },
    {
      title: "内容",
      dataIndex: "content",
      render: (_, row) => {
        const content = Array.isArray(row.content)
          ? row.content.join("\n")
          : row.content;
        return renderTextWithDetails(content);
      },
    },
    {
      title: "提示词",
      dataIndex: "prompt",
      render: (_, row) => renderTextWithDetails(row.prompt),
    },
    {
      title: "溯源",
      dataIndex: "trace",
      width: 100,
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => showTraceModal(row)}>
          详情
        </Button>
      ),
    },
  ];

  const freeChatColumns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 200, 
    },
    {
      title: "时间",
      dataIndex: "createTime",
      width: 200,
    },
    {
      title: "审核状态",
      dataIndex: "status",
      valueEnum: {
        "PASSED": {
          text: "通过",
          status: "Success",
        },
        "REJECTED": {
          text: "未通过",
          status: "Error",
        },
      },
      width: 100,
    },
    {
      title: "IP",
      dataIndex: "clientIp",
      width: 100,
    },
    {
      title: "内容",
      dataIndex: "content",
      render: (_, row) => {
        const content = Array.isArray(row.content)
          ? row.content.join("\n")
          : row.content;
        return renderTextWithDetails(content);
      },
    },
    {
      title: "提示词",
      dataIndex: "prompt",
      render: (_, row) => renderTextWithDetails(row.prompt),
    },
    {
      title: "溯源",
      dataIndex: "trace",
      width: 100,
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => showTraceModal(row)}>
          详情
        </Button>
      ),
    },
  ];

  const aiColumns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 100, 
    },
    {
      title: "时间",
      dataIndex: "createTime",
      width: 200,
    },
    {
      title: "审核状态",
      dataIndex: "status",
      valueEnum: {
        "PASSED": {
          text: "通过",
          status: "Success",
        },
        "REJECTED": {
          text: "未通过",
          status: "Error",
        },
      },
      width: 100,
    },
    {
      title: "IP",
      dataIndex: "clientIp",
      width: 100,
    },
    {
      title: "内容",
      dataIndex: "images",
      maxWidth: 200,
      ellipsis: true,
      render: (_, row) => {
        const images = row.images;
        if (!Array.isArray(images) || images.length === 0) {
          return "-";
        }
        
        // 智能检测URL的媒体类型
        const detectMediaType = (url) => {
          if (!url || typeof url !== 'string') return 'unknown';
          
          // 检查文件扩展名
          const videoExts = /\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)(\?.*)?$/i;
          const audioExts = /\.(mp3|wav|ogg|aac|flac|m4a|wma)(\?.*)?$/i;
          const imageExts = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i;
          
          if (videoExts.test(url)) return 'video';
          if (audioExts.test(url)) return 'audio';
          if (imageExts.test(url)) return 'image';
          
          // 检查URL路径中的关键词
          if (/video|mp4|avi|mov/i.test(url)) return 'video';
          if (/audio|mp3|wav|sound/i.test(url)) return 'audio';
          if (/image|img|photo|picture/i.test(url)) return 'image';
          
          return 'unknown';
        };
        
        // 分类媒体文件
        const imageUrls = [];
        const videoUrls = [];
        const audioUrls = [];
        
        images.forEach(url => {
          // 跳过无效的URL
          if (!isValidUrl(url)) return;
          
          const mediaType = detectMediaType(url);
          
          switch (mediaType) {
            case 'video':
              videoUrls.push(url);
              break;
            case 'audio':
              audioUrls.push(url);
              break;
            case 'image':
              imageUrls.push(url);
              break;
            default:
              // 如果无法确定类型，检查上下文
              const prompt = row.prompt || '';
              
              if (/SaveVideo|video|视频/i.test(prompt)) {
                videoUrls.push(url);
              } else if (/SaveAudio|audio|音频/i.test(prompt)) {
                audioUrls.push(url);
              } else {
                imageUrls.push(url);
              }
          }
        });
        
        // 优先显示视频
        if (videoUrls.length > 0) {
          return (
            <Space size="small">
              {videoUrls.slice(0, 2).map((url, index) => (
                <EnhancedVideo
                  key={index}
                  width={120}
                  height={120}
                  src={url}
                  controls
                  preload="metadata"
                  style={{ borderRadius: 4, objectFit: 'cover' }}
                />
              ))}
              {videoUrls.length > 2 && (
                <span style={{ fontSize: 12, color: '#999' }}>+{videoUrls.length - 2}</span>
              )}
            </Space>
          );
        }
        
        // 然后显示音频
        if (audioUrls.length > 0) {
          return (
            <Space size="small" direction="vertical">
              {audioUrls.slice(0, 2).map((url, index) => (
                <EnhancedAudio
                  key={index}
                  src={url}
                  controls
                  preload="metadata"
                  style={{ width: 150, height: 30 }}
                />
              ))}
              {audioUrls.length > 2 && (
                <span style={{ fontSize: 12, color: '#999' }}>+{audioUrls.length - 2}</span>
              )}
            </Space>
          );
        }
        
        // 最后显示图片
        if (imageUrls.length > 0) {
          return (
            <Image.PreviewGroup>
              <Space size="small">
                {imageUrls.slice(0, 3).map((url, index) => (
                  <EnhancedImage
                    key={index}
                    width={120}
                    height={120}
                    src={url}
                    alt={`image-${index}`}
                    style={{ borderRadius: 4 }}
                  />
                ))}
                {imageUrls.length > 3 && (
                  <span style={{ fontSize: 12, color: '#999' }}>+{imageUrls.length - 3}</span>
                )}
              </Space>
            </Image.PreviewGroup>
          );
        }
        
        return "-";
      },
    },
    {
      title: "提示词",
      dataIndex: "prompt",
      width: 200,
      render: (_, row) => renderTextWithDetails(row.prompt),
    },
    {
      title: "溯源",
      dataIndex: "trace",
      width: 100,
      render: (_, row) => (
        <Button type="link" size="small" onClick={() => showTraceModal(row)}>
          详情
        </Button>
      ),
    },
  ];

  // Use useMemo to select columns based on type
  const columns = useMemo(() => {
    switch (type) {
      case "APP":
        return appColumns;
      case "CONVERSATION":
        return freeChatColumns;
      case "AI":
        return aiColumns;
      default:
        return []; // Default or error case
    }
  }, [type]);

  const handleTypeChange = (e) => {
    setType(e.target.value);
  };

  const handleSearch = (value) => {
    setKeyword(value);
  };

  // Custom request function to manage loading state
  const fetchData = async (params) => {
    setLoading(true);
    try {
      const res = await getContentList(params);
      return res;
    } finally {
      setLoading(false);
    }
  };

  const showModal = (content) => {
    setModalContent(content || "");
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalContent("");
  };

  const showTraceModal = (rowData) => {
    setTraceData(rowData);
    setIsTraceModalOpen(true);
  };

  const handleTraceModalClose = () => {
    setIsTraceModalOpen(false);
    setTraceData(null);
    setShowRawData(false);
  };

  // --- Reusable Render Function ---
  const renderTextWithDetails = (text) => {
    const content = String(text || ""); // Ensure it's a string
    const isLong = content.length > 10;
    return (
      <Space size="small">
        <Text ellipsis={isLong ? { tooltip: false } : false}>
          {isLong ? `${content.substring(0, 10)}...` : content}
        </Text>
        {/* Conditionally render button only if there is content */}
        {content && (
          <Button type="link" size="small" onClick={() => showModal(content)}>
            详情
          </Button>
        )}
      </Space>
    );
  };

  return (
    <PageContainer title="内容管理">
      <Space style={{ marginBottom: 16 }}>
        <Radio.Group value={type} onChange={handleTypeChange} disabled={loading}>
          <Radio.Button value="APP">APP</Radio.Button>
          <Radio.Button value="CONVERSATION">自由会话</Radio.Button>
          <Radio.Button value="AI">AI</Radio.Button>
        </Radio.Group>
        <Input.Search
          placeholder="输入关键字搜索"
          onSearch={handleSearch}
          onChange={(e) => {
            if (e.target.value === "") {
              handleSearch("");
            }
          }}
          style={{ width: 200 }}
          allowClear
          disabled={loading}
          enterButton
        />
      </Space>
      <ProTable
        columns={columns}
        actionRef={actionRef}
        request={fetchData}
        rowKey="id"
        search={false}
        options={false}
        params={{ type, keyword }}
        pagination={{
          defaultPageSize: 10,
        }}
        loading={loading}
      />
      <Modal
        title="详细内容"
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        destroyOnClose
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
          {modalContent}
        </div>
      </Modal>
      
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>溯源信息</span>
            <Button
              size="small"
              type={showRawData ? "primary" : "default"}
              onClick={() => setShowRawData(!showRawData)}
              style={{ marginRight: 40 }}
            >
              {showRawData ? "格式化显示" : "原始数据"}
            </Button>
          </div>
        }
        open={isTraceModalOpen}
        onCancel={handleTraceModalClose}
        footer={null}
        destroyOnClose
        width={1000}
      >
        {traceData && (
          <div style={{ padding: '16px 0' }}>
            <div style={{ display: 'flex', gap: '24px', marginBottom: 24, justifyContent: 'space-between', paddingLeft: '20px', paddingRight: '20px' }}>
              <div style={{ flex: '0 0 48%', maxWidth: '48%' }}>
                <Text strong>输入：</Text>
                <div style={{ 
                  marginTop: 8, 
                  padding: 12, 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 6,
                  maxHeight: 300,
                  overflowY: 'auto',
                  wordBreak: 'break-word'
                }}>
                  {(() => {
                    const prompt = traceData.prompt || "";
                    
                    // 如果显示原始数据，直接返回原始内容
                    if (showRawData) {
                      return (
                        <Text style={{ fontSize: 12, color: '#666', whiteSpace: 'pre-wrap' }}>
                          {prompt || '暂无数据'}
                        </Text>
                      );
                    }
                    
                    // 智能解析和格式化输入内容
                    const parseInputContent = (content) => {
                      if (!content) return null;
                      
                      try {
                        // 尝试解析JSON
                        const parsed = JSON.parse(content);
                        const result = {
                          text: [],
                          images: [],
                          videos: [],
                          audios: [],
                          operations: []
                        };
                        
                        // 递归提取有用信息
                        const extractInfo = (obj, path = '') => {
                          if (typeof obj === 'string') {
                            // 检查是否是URL
                            if (/^https?:\/\//.test(obj) && isValidUrl(obj)) {
                              if (/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i.test(obj)) {
                                result.images.push(obj);
                              } else if (/\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)(\?.*)?$/i.test(obj)) {
                                result.videos.push(obj);
                              } else if (/\.(mp3|wav|ogg|aac|flac|m4a|wma)(\?.*)?$/i.test(obj)) {
                                result.audios.push(obj);
                              }
                            } else if (obj.length > 2 && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(obj)) {
                              // 可能是有意义的文本
                              result.text.push(obj);
                            }
                          } else if (Array.isArray(obj)) {
                            obj.forEach((item, index) => extractInfo(item, `${path}[${index}]`));
                          } else if (obj && typeof obj === 'object') {
                            // 检查特殊字段
                            if (obj.displayName && typeof obj.displayName === 'string') {
                              result.operations.push(obj.displayName);
                            }
                            if (obj.name && typeof obj.name === 'string' && /Save(Video|Audio|Image)/i.test(obj.name)) {
                              result.operations.push(`操作: ${obj.name}`);
                            }
                            if (obj.type && typeof obj.type === 'string') {
                              if (['SaveVideo', 'SaveAudio', 'SaveImage'].includes(obj.type)) {
                                result.operations.push(`类型: ${obj.type}`);
                              }
                            }
                            
                            Object.entries(obj).forEach(([key, value]) => {
                              extractInfo(value, path ? `${path}.${key}` : key);
                            });
                          }
                        };
                        
                        extractInfo(parsed);
                        return result;
                      } catch (e) {
                        // 如果不是JSON，尝试提取URL和文本
                        const result = {
                          text: [],
                          images: [],
                          videos: [],
                          audios: [],
                          operations: []
                        };
                        
                        // 提取URL
                        const urls = content.match(/https?:\/\/[^\s"'<>()]+/gi) || [];
                        urls.forEach(url => {
                          // 只处理有效的URL
                          if (!isValidUrl(url)) return;
                          
                          if (/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i.test(url)) {
                            result.images.push(url);
                          } else if (/\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)(\?.*)?$/i.test(url)) {
                            result.videos.push(url);
                          } else if (/\.(mp3|wav|ogg|aac|flac|m4a|wma)(\?.*)?$/i.test(url)) {
                            result.audios.push(url);
                          }
                        });
                        
                        // 提取操作信息
                        if (/SaveVideo/i.test(content)) result.operations.push('保存视频');
                        if (/SaveAudio/i.test(content)) result.operations.push('保存音频');
                        if (/SaveImage/i.test(content)) result.operations.push('保存图片');
                        
                        // 如果没有找到特殊内容，保留原始文本的简化版本
                        if (result.images.length === 0 && result.videos.length === 0 && 
                            result.audios.length === 0 && result.operations.length === 0) {
                          // 保持原始格式，只移除多余的括号和引号，但保留换行符
                          const cleanText = content.replace(/[{}"\[\]]/g, '')
                                                  .trim();
                          if (cleanText.length > 10) {
                            result.text.push(cleanText);
                          }
                        }
                        
                        return result;
                      }
                    };
                    
                    const parsedInput = parseInputContent(prompt);
                    
                    if (!parsedInput) {
                      return <Text>{prompt}</Text>;
                    }
                    
                    return (
                      <Space direction="vertical" style={{ width: '100%' }} size="small">
                        {/* 显示操作信息 */}
                        {parsedInput.operations.length > 0 && (
                          <div>
                            <Text strong style={{ fontSize: 12, color: '#1890ff' }}>操作：</Text>
                            <div style={{ marginTop: 4 }}>
                              {parsedInput.operations.map((op, index) => (
                                <Text key={index} code style={{ marginRight: 8, fontSize: 11 }}>
                                  {op}
                                </Text>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* 显示文本内容 */}
                        {parsedInput.text.length > 0 && (
                          <div>
                            <Text strong style={{ fontSize: 12, color: '#52c41a' }}>文本：</Text>
                            <div style={{ marginTop: 4 }}>
                              {parsedInput.text.map((text, index) => (
                                <div key={index} style={{ 
                                  padding: 4, 
                                  backgroundColor: '#fff', 
                                  borderRadius: 4, 
                                  marginBottom: 4,
                                  fontSize: 11,
                                  lineHeight: 1.4,
                                  whiteSpace: 'pre-wrap',
                                  position: 'relative'
                                }}>
                                  {text}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* 显示图片 */}
                        {parsedInput.images.length > 0 && (
                          <div>
                            <Text strong style={{ fontSize: 12, color: '#722ed1' }}>图片：</Text>
                            <div style={{ marginTop: 4 }}>
                              <Space wrap size="small">
                                {parsedInput.images.slice(0, 3).map((url, index) => (
                                  <EnhancedImage
                                    key={index}
                                    width={120}
                                    height={120}
                                    src={url}
                                    alt={`输入图片-${index + 1}`}
                                    style={{ borderRadius: 4, objectFit: 'cover' }}
                                  />
                                ))}
                                {parsedInput.images.length > 3 && (
                                  <span style={{ fontSize: 11, color: '#999' }}>+{parsedInput.images.length - 3}</span>
                                )}
                              </Space>
                            </div>
                          </div>
                        )}
                        
                        {/* 显示视频 */}
                        {parsedInput.videos.length > 0 && (
                          <div>
                            <Text strong style={{ fontSize: 12, color: '#f5222d' }}>视频：</Text>
                            <div style={{ marginTop: 4 }}>
                              <Space wrap size="small">
                                {parsedInput.videos.slice(0, 2).map((url, index) => (
                                  <EnhancedVideo
                                    key={index}
                                    width={140}
                                    height={105}
                                    src={url}
                                    style={{ borderRadius: 4, objectFit: 'cover' }}
                                  />
                                ))}
                                {parsedInput.videos.length > 2 && (
                                  <span style={{ fontSize: 11, color: '#999' }}>+{parsedInput.videos.length - 2}</span>
                                )}
                              </Space>
                            </div>
                          </div>
                        )}
                        
                        {/* 显示音频 */}
                        {parsedInput.audios.length > 0 && (
                          <div>
                            <Text strong style={{ fontSize: 12, color: '#fa8c16' }}>音频：</Text>
                            <div style={{ marginTop: 4 }}>
                              <Space direction="vertical" size="small">
                                {parsedInput.audios.slice(0, 2).map((url, index) => (
                                  <EnhancedAudio
                                    key={index}
                                    src={url}
                                    style={{ width: '100%', maxWidth: 450, height: 40 }}
                                  />
                                ))}
                                {parsedInput.audios.length > 2 && (
                                  <span style={{ fontSize: 11, color: '#999' }}>+{parsedInput.audios.length - 2}</span>
                                )}
                              </Space>
                            </div>
                          </div>
                        )}
                        
                        {/* 如果没有任何有用信息，显示原始内容的简化版本 */}
                        {parsedInput.operations.length === 0 && 
                         parsedInput.text.length === 0 && 
                         parsedInput.images.length === 0 && 
                         parsedInput.videos.length === 0 && 
                         parsedInput.audios.length === 0 && (
                          <Text style={{ fontSize: 11, color: '#999' }}>
                            {prompt.length > 100 ? prompt.substring(0, 100) + '...' : prompt}
                          </Text>
                        )}
                      </Space>
                    );
                  })()}
                </div>
              </div>
              
              <div style={{ flex: '0 0 48%', maxWidth: '48%' }}>
                <Text strong>输出：</Text>
                <div style={{ 
                  marginTop: 8, 
                  padding: 12, 
                  backgroundColor: '#f5f5f5', 
                  borderRadius: 6,
                  maxHeight: 300,
                  overflowY: 'auto',
                  wordBreak: 'break-word'
                }}>
                  {(() => {
                    // 如果显示原始数据，直接返回原始内容
                    if (showRawData) {
                      const rawContent = Array.isArray(traceData.content) ? traceData.content.join('\n') : (traceData.content || '');
                      const images = traceData.images || [];
                      const videos = traceData.videos || [];
                      const audios = traceData.audios || [];
                      
                      // 构建完整的原始数据显示
                      const rawDataParts = [];
                      
                      if (rawContent) {
                        rawDataParts.push(`内容: ${rawContent}`);
                      }
                      
                      if (images.length > 0) {
                        rawDataParts.push(`图片URLs (${images.length}个):\n${images.map((url, i) => `${i + 1}. ${url}`).join('\n')}`);
                      }
                      
                      if (videos.length > 0) {
                        rawDataParts.push(`视频URLs (${videos.length}个):\n${videos.map((url, i) => `${i + 1}. ${url}`).join('\n')}`);
                      }
                      
                      if (audios.length > 0) {
                        rawDataParts.push(`音频URLs (${audios.length}个):\n${audios.map((url, i) => `${i + 1}. ${url}`).join('\n')}`);
                      }
                      
                      const finalRawData = rawDataParts.length > 0 ? rawDataParts.join('\n\n') : '暂无数据';
                      
                      return (
                        <Text style={{ fontSize: 12, color: '#666', whiteSpace: 'pre-wrap' }}>
                          {finalRawData}
                        </Text>
                      );
                    }
                    
                    // 智能检测URL的媒体类型
                    const detectMediaType = (url) => {
                      if (!url || typeof url !== 'string') return 'unknown';
                      
                      // 检查文件扩展名
                      const videoExts = /\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)(\?.*)?$/i;
                      const audioExts = /\.(mp3|wav|ogg|aac|flac|m4a|wma)(\?.*)?$/i;
                      const imageExts = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i;
                      
                      if (videoExts.test(url)) return 'video';
                      if (audioExts.test(url)) return 'audio';
                      if (imageExts.test(url)) return 'image';
                      
                      // 检查URL路径中的关键词
                      if (/video|mp4|avi|mov/i.test(url)) return 'video';
                      if (/audio|mp3|wav|sound/i.test(url)) return 'audio';
                      if (/image|img|photo|picture/i.test(url)) return 'image';
                      
                      return 'unknown';
                    };
                    
                    // 通用媒体渲染器
                    const renderMedia = (urls, type = 'video') => {
                      if (!urls || urls.length === 0) return null;
                      
                      return (
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {urls.slice(0, 5).map((url, index) => {
                            const cleanUrl = typeof url === 'string' ? url.trim() : String(url).trim();
                            
                            return (
                              <div key={index} style={{ marginBottom: urls.length > 1 ? 8 : 0 }}>
                                {type === 'video' ? (
                                  <EnhancedVideo
                                    width="100%"
                                    src={cleanUrl}
                                    style={{ borderRadius: 8, maxWidth: 200 }}
                                  />
                                ) : type === 'audio' ? (
                                  <EnhancedAudio
                                    src={cleanUrl}
                                    style={{ width: '100%', maxWidth: 280, height: 40 }}
                                  />
                                ) : (
                                  <EnhancedImage
                                    width="100%"
                                    src={cleanUrl}
                                    alt={`媒体文件-${index + 1}`}
                                    style={{ borderRadius: 8, maxWidth: 200 }}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </Space>
                      );
                    };
                    
                    // 1. 智能处理images字段 - 可能包含视频文件
                    if (traceData.images && traceData.images.length > 0) {
                      // 分类images数组中的URL
                      const imageUrls = [];
                      const videoUrls = [];
                      const audioUrls = [];
                      
                      traceData.images.forEach(url => {
                        // 跳过无效的URL
                        if (!isValidUrl(url)) {
                          return;
                        }
                        
                        const mediaType = detectMediaType(url);
                        
                        switch (mediaType) {
                          case 'video':
                            videoUrls.push(url);
                            break;
                          case 'audio':
                            audioUrls.push(url);
                            break;
                          case 'image':
                            imageUrls.push(url);
                            break;
                          default:
                            // 如果无法确定类型，检查上下文
                            const rawContent = Array.isArray(traceData.content) ? traceData.content.join('\n') : (traceData.content || '');
                            const prompt = traceData.prompt || '';
                            const allText = rawContent + ' ' + prompt;
                            
                            if (/SaveVideo|video|视频/i.test(allText)) {
                              videoUrls.push(url);
                            } else if (/SaveAudio|audio|音频/i.test(allText)) {
                              audioUrls.push(url);
                            } else {
                              imageUrls.push(url);
                            }
                        }
                      });
                      
                      // 优先显示视频
                      if (videoUrls.length > 0) {
                        return renderMedia(videoUrls, 'video');
                      }
                      
                      // 然后显示音频
                      if (audioUrls.length > 0) {
                        return renderMedia(audioUrls, 'audio');
                      }
                      
                      // 最后显示图片
                      if (imageUrls.length > 0) {
                        return imageUrls.length === 1 ? (
                          <EnhancedImage
                            width="100%"
                            src={imageUrls[0]}
                            alt="生成的图片"
                            style={{ borderRadius: 8, maxWidth: 200 }}
                          />
                        ) : (
                          <Image.PreviewGroup>
                            <Space wrap>
                              {imageUrls.map((url, index) => (
                                <EnhancedImage
                                  key={index}
                                  width={80}
                                  src={url}
                                  alt={`生成的图片-${index + 1}`}
                                  style={{ borderRadius: 8 }}
                                />
                              ))}
                            </Space>
                          </Image.PreviewGroup>
                        );
                      }
                    }
                    
                    // 2. 检查专用视频和音频字段
                    if (traceData.videos && traceData.videos.length > 0) {
                      return renderMedia(traceData.videos, 'video');
                    }
                    
                    if (traceData.audios && traceData.audios.length > 0) {
                      return renderMedia(traceData.audios, 'audio');
                    }
                    
                    // 3. 解析和分析内容（包括prompt）
                    const rawContent = Array.isArray(traceData.content) ? traceData.content.join('\n') : (traceData.content || '');
                    const prompt = traceData.prompt || '';
                    const allText = rawContent + ' ' + prompt;
                    
                    // 尝试解析JSON（优先从prompt解析）
                    let parsedData = null;
                    const textToParse = prompt || rawContent;
                    try {
                      if (textToParse && (textToParse.trim().startsWith('{') || textToParse.trim().startsWith('['))) {
                        parsedData = JSON.parse(textToParse);
                      }
                    } catch (e) {
                      // 忽略JSON解析错误
                    }
                    
                    // 4. 提取所有可能的URL
                    const extractAllUrls = (text, obj = null) => {
                      const urls = new Set();
                      
                      // 从文本中提取URL
                      if (typeof text === 'string') {
                        const urlMatches = text.match(/https?:\/\/[^\s"'<>()]+/gi);
                        if (urlMatches) {
                          urlMatches.forEach(url => {
                            const cleanUrl = url.replace(/[.,;!?]$/, '');
                            if (isValidUrl(cleanUrl)) {
                              urls.add(cleanUrl);
                            }
                          });
                        }
                      }
                      
                      // 从对象中递归提取URL
                      const extractFromObject = (obj) => {
                        if (typeof obj === 'string' && /^https?:\/\//.test(obj) && isValidUrl(obj)) {
                          urls.add(obj);
                        } else if (Array.isArray(obj)) {
                          obj.forEach(extractFromObject);
                        } else if (obj && typeof obj === 'object') {
                          Object.values(obj).forEach(extractFromObject);
                        }
                      };
                      
                      if (obj) extractFromObject(obj);
                      
                      return Array.from(urls);
                    };
                    
                    const allUrls = extractAllUrls(allText, parsedData);
                    
                    // 5. 分类URL
                    const videoUrls = [];
                    const audioUrls = [];
                    const imageUrls = [];
                    const otherUrls = [];
                    
                    allUrls.forEach(url => {
                      const mediaType = detectMediaType(url);
                      switch (mediaType) {
                        case 'video': videoUrls.push(url); break;
                        case 'audio': audioUrls.push(url); break;
                        case 'image': imageUrls.push(url); break;
                        default: otherUrls.push(url);
                      }
                    });
                    
                    // 6. 检查关键词
                    const hasVideoKeywords = /SaveVideo|video|mp4|avi|mov|wmv|flv|webm|h265|视频/i.test(allText);
                    const hasAudioKeywords = /SaveAudio|audio|mp3|wav|ogg|aac|flac|m4a|音频/i.test(allText);
                    
                    // 7. 渲染逻辑
                    if (videoUrls.length > 0) {
                      return renderMedia(videoUrls, 'video');
                    }
                    
                    if (audioUrls.length > 0) {
                      return renderMedia(audioUrls, 'audio');
                    }
                    
                    // 如果有关键词但没有明确的媒体URL，尝试所有URL
                    if (hasVideoKeywords && otherUrls.length > 0) {
                      return renderMedia(otherUrls, 'video');
                    }
                    
                    if (hasAudioKeywords && otherUrls.length > 0) {
                      return renderMedia(otherUrls, 'audio');
                    }
                    
                    // 显示图片
                    if (imageUrls.length > 0) {
                      return imageUrls.length === 1 ? (
                        <EnhancedImage
                          width="100%"
                          src={imageUrls[0]}
                          alt="生成的图片"
                          style={{ borderRadius: 8, maxWidth: 200 }}
                        />
                      ) : (
                        <Image.PreviewGroup>
                          <Space wrap>
                            {imageUrls.map((url, index) => (
                              <EnhancedImage
                                key={index}
                                width={80}
                                src={url}
                                alt={`生成的图片-${index + 1}`}
                                style={{ borderRadius: 8 }}
                              />
                            ))}
                          </Space>
                        </Image.PreviewGroup>
                      );
                    }
                    
                    // 8. JSON格式化显示（类似输入部分）
                    const parseOutputContent = (content) => {
                      if (!content) return null;
                      
                      try {
                        // 尝试解析JSON
                        const parsed = JSON.parse(content);
                        const result = {
                          text: [],
                          images: [],
                          videos: [],
                          audios: [],
                          operations: []
                        };
                        
                        // 递归提取有用信息
                        const extractInfo = (obj, path = '') => {
                          if (typeof obj === 'string') {
                            // 检查是否是URL
                            if (/^https?:\/\//.test(obj) && isValidUrl(obj)) {
                              if (/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i.test(obj)) {
                                result.images.push(obj);
                              } else if (/\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)(\?.*)?$/i.test(obj)) {
                                result.videos.push(obj);
                              } else if (/\.(mp3|wav|ogg|aac|flac|m4a|wma)(\?.*)?$/i.test(obj)) {
                                result.audios.push(obj);
                              }
                            } else if (obj.length > 2 && !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(obj)) {
                              // 可能是有意义的文本
                              result.text.push(obj);
                            }
                          } else if (Array.isArray(obj)) {
                            obj.forEach((item, index) => extractInfo(item, `${path}[${index}]`));
                          } else if (obj && typeof obj === 'object') {
                            // 检查特殊字段
                            if (obj.displayName && typeof obj.displayName === 'string') {
                              result.operations.push(obj.displayName);
                            }
                            if (obj.name && typeof obj.name === 'string' && /Save(Video|Audio|Image)/i.test(obj.name)) {
                              result.operations.push(`操作: ${obj.name}`);
                            }
                            if (obj.type && typeof obj.type === 'string') {
                              if (['SaveVideo', 'SaveAudio', 'SaveImage'].includes(obj.type)) {
                                result.operations.push(`类型: ${obj.type}`);
                              }
                            }
                            
                            Object.entries(obj).forEach(([key, value]) => {
                              extractInfo(value, path ? `${path}.${key}` : key);
                            });
                          }
                        };
                        
                        extractInfo(parsed);
                        return result;
                      } catch (e) {
                        // 如果不是JSON，尝试提取URL和文本
                        const result = {
                          text: [],
                          images: [],
                          videos: [],
                          audios: [],
                          operations: []
                        };
                        
                        // 提取URL
                        const urls = content.match(/https?:\/\/[^\s"'<>()]+/gi) || [];
                        urls.forEach(url => {
                          // 只处理有效的URL
                          if (!isValidUrl(url)) return;
                          
                          if (/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i.test(url)) {
                            result.images.push(url);
                          } else if (/\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v)(\?.*)?$/i.test(url)) {
                            result.videos.push(url);
                          } else if (/\.(mp3|wav|ogg|aac|flac|m4a|wma)(\?.*)?$/i.test(url)) {
                            result.audios.push(url);
                          }
                        });
                        
                        // 提取操作信息
                        if (/SaveVideo/i.test(content)) result.operations.push('保存视频');
                        if (/SaveAudio/i.test(content)) result.operations.push('保存音频');
                        if (/SaveImage/i.test(content)) result.operations.push('保存图片');
                        
                        // 如果没有找到特殊内容，保留原始文本的简化版本
                        if (result.images.length === 0 && result.videos.length === 0 && 
                            result.audios.length === 0 && result.operations.length === 0) {
                          // 保持原始格式，只移除多余的括号和引号，但保留换行符
                          const cleanText = content.replace(/[{}"\[\]]/g, '')
                                                  .trim();
                          if (cleanText.length > 10) {
                            result.text.push(cleanText);
                          }
                        }
                        
                        return result;
                      }
                    };
                    
                    // 尝试解析输出内容
                    const parsedOutput = parseOutputContent(rawContent);
                    
                    if (parsedOutput && (parsedOutput.operations.length > 0 || parsedOutput.text.length > 0 || 
                        parsedOutput.images.length > 0 || parsedOutput.videos.length > 0 || parsedOutput.audios.length > 0)) {
                      return (
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          {/* 显示操作信息 */}
                          {parsedOutput.operations.length > 0 && (
                            <div>
                              <Text strong style={{ fontSize: 12, color: '#1890ff' }}>操作：</Text>
                              <div style={{ marginTop: 4 }}>
                                {parsedOutput.operations.map((op, index) => (
                                  <Text key={index} code style={{ marginRight: 8, fontSize: 11 }}>
                                    {op}
                                  </Text>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* 显示文本内容 */}
                          {parsedOutput.text.length > 0 && (
                            <div>
                              <Text strong style={{ fontSize: 12, color: '#52c41a' }}>输出内容：</Text>
                              <div style={{ marginTop: 4 }}>
                                {parsedOutput.text.map((text, index) => (
                                  <div key={index} style={{ 
                                    padding: 4, 
                                    backgroundColor: '#fff', 
                                    borderRadius: 4, 
                                    marginBottom: 4,
                                    fontSize: 11,
                                    lineHeight: 1.4,
                                    whiteSpace: 'pre-wrap',
                                    position: 'relative'
                                  }}>
                                    {text}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* 显示图片 */}
                          {parsedOutput.images.length > 0 && (
                            <div>
                              <Text strong style={{ fontSize: 12, color: '#722ed1' }}>生成图片：</Text>
                              <div style={{ marginTop: 4 }}>
                                <Space wrap size="small">
                                  {parsedOutput.images.slice(0, 3).map((url, index) => (
                                    <EnhancedImage
                                      key={index}
                                      width={120}
                                      height={120}
                                      src={url}
                                      alt={`生成图片-${index + 1}`}
                                      style={{ borderRadius: 4, objectFit: 'cover' }}
                                    />
                                  ))}
                                  {parsedOutput.images.length > 3 && (
                                    <span style={{ fontSize: 11, color: '#999' }}>+{parsedOutput.images.length - 3}</span>
                                  )}
                                </Space>
                              </div>
                            </div>
                          )}
                          
                          {/* 显示视频 */}
                          {parsedOutput.videos.length > 0 && (
                            <div>
                              <Text strong style={{ fontSize: 12, color: '#f5222d' }}>生成视频：</Text>
                              <div style={{ marginTop: 4 }}>
                                <Space wrap size="small">
                                  {parsedOutput.videos.slice(0, 2).map((url, index) => (
                                    <EnhancedVideo
                                      key={index}
                                      width={140}
                                      height={105}
                                      src={url}
                                      style={{ borderRadius: 4, objectFit: 'cover' }}
                                    />
                                  ))}
                                  {parsedOutput.videos.length > 2 && (
                                    <span style={{ fontSize: 11, color: '#999' }}>+{parsedOutput.videos.length - 2}</span>
                                  )}
                                </Space>
                              </div>
                            </div>
                          )}
                          
                          {/* 显示音频 */}
                          {parsedOutput.audios.length > 0 && (
                            <div>
                              <Text strong style={{ fontSize: 12, color: '#fa8c16' }}>生成音频：</Text>
                              <div style={{ marginTop: 4 }}>
                                <Space direction="vertical" size="small">
                                  {parsedOutput.audios.slice(0, 2).map((url, index) => (
                                    <EnhancedAudio
                                      key={index}
                                      src={url}
                                      style={{ width: '100%', maxWidth: 450, height: 40 }}
                                    />
                                  ))}
                                  {parsedOutput.audios.length > 2 && (
                                    <span style={{ fontSize: 11, color: '#999' }}>+{parsedOutput.audios.length - 2}</span>
                                  )}
                                </Space>
                              </div>
                            </div>
                          )}
                        </Space>
                      );
                    }
                    
                    // 9. 默认显示
                    return (
                      <div style={{ 
                        minHeight: 120, 
                        border: '1px dashed #d9d9d9', 
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        padding: 12,
                        backgroundColor: '#f5f5f5',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {rawContent || prompt || '暂无内容'}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
            <Divider />
            <div style={{ padding: 16, paddingTop: 0 }}>
              <Text strong>溯源内容：</Text>
              <div style={{ marginTop: 12 }}>
                <div style={{ marginBottom: 8 }}>
                  <Text>用户ID：</Text>
                  <Text code>{traceData.userId || ""}</Text>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Text>生成者IP：</Text>
                  <Text code>{traceData.clientIp || ""}</Text>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Text>生成时间：</Text>
                  <Text code>{traceData.createTime || ""}</Text>
                </div>
                <div>
                  <Text>企业机构代码：</Text>
                  <Text code>91330106MA7H25KC5M</Text>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}
